/**
 * LandingPage: Premium public marketing page for Stratakey.
 *
 * Sections:
 *  1. Sticky Glassmorphism Navbar
 *  2. Hero (dark navy, gradient glows, headline + CTAs)
 *  3. Social Proof Stats Bar
 *  4. Features Grid (6 feature cards)
 *  5. How It Works (3-step process)
 *  6. Role-Based Plans (Strata Council vs Property Manager)
 *  7. Testimonials
 *  8. Full-Width CTA Banner
 *  9. Footer
 */

import { useState, useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useCurrency } from "@/hooks/useCurrency";
import { CurrencyToggle } from "@/components/CurrencyToggle";
import {
  trackCtaClick, trackPricingView, trackPlanSelected,
  trackBuildingCountChanged, trackFaqOpened,
  initScrollDepthTracking, initOutboundLinkTracking,
} from "@/lib/ga4";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Building2,
  UserCog,
  Home,
  Shield,
  Bell,
  BarChart3,
  Users,
  ClipboardCheck,
  CalendarCheck,
  Menu,
  X,
  Sparkles,
  Star,
  ChevronRight,
  Lock,
  Zap,
  MessageSquare,
  Minus,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CanadaMap } from "@/components/CanadaMap";



// ─── Navbar ───────────────────────────────────────────────────────────────────

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Pricing", href: "#pricing" },
    { label: "For Hosts", href: "#pricing" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-sm border-b border-slate-200/80 dark:border-slate-700/80"
          : "bg-transparent border-b border-white/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5 group">
            <img
              src="/logo.png"
              alt="Stratakey"
              className="h-8 w-auto object-contain rounded-xl drop-shadow-sm group-hover:scale-105 transition-transform"
            />
            <span
              className={`font-bold text-lg tracking-tight transition-colors ${
                scrolled ? "text-slate-900 dark:text-white" : "text-white"
              }`}
            >
              Stratakey
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  scrolled
                    ? "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                    : "text-white/70 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {/* Back button — only visible after user scrolls away from the hero */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className={`flex cursor-pointer items-center gap-1 text-sm font-medium px-3 py-2 rounded-lg transition-all duration-300 ${
                scrolled
                  ? "opacity-100 pointer-events-auto text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                  : "opacity-0 pointer-events-none text-white/70"
              }`}
              tabIndex={scrolled ? 0 : -1}
            >
              <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
              Back to Top
            </button>
            <Link
              to="/login"
              className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                scrolled
                  ? "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Sign In
            </Link>
            <Link to="/signup">
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all font-medium"
              >
                Get Started Free
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          className={`md:hidden cursor-pointer p-2 rounded-lg transition-colors ${
              scrolled ? "text-slate-700 dark:text-slate-200" : "text-slate-700"
            }`}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 py-4 px-2 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex flex-col gap-2 mt-2">
              {/* Back button in mobile menu — only when user has scrolled down */}
              {scrolled && (
                <button
                  onClick={() => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="flex cursor-pointer items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-left w-full"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to Top
                </button>
              )}
              <Link
                to="/login"
                className="block px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Sign In
              </Link>
              <Link to="/signup">
                <Button className="w-full" size="sm">
                  Get Started Free
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

// ─── Hero Section ─────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section id="main-content" className="hero-grain hero-shimmer relative min-h-screen flex items-center overflow-hidden bg-[hsl(222,47%,4%)]">

      {/* ── Glass background layers ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">

        {/* Vivid aurora orbs — the color visible through the glass */}
        <div className="hero-orb-1 absolute -top-60 -left-60 w-[800px] h-[800px] rounded-full bg-blue-500/40 blur-[130px]" />
        <div className="hero-orb-2 absolute -bottom-40 -right-20 w-[650px] h-[650px] rounded-full bg-violet-600/35 blur-[110px]" />
        <div className="hero-orb-3 absolute top-1/4 left-1/2 w-[550px] h-[550px] rounded-full bg-cyan-500/15 blur-[100px]" />

        {/* Glass grid — faint lines that give the surface a pane texture */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)`,
            backgroundSize: "100px 100px",
          }}
        />

        {/* Canada map — decorative geographic backdrop */}
        <div className="absolute inset-0 flex items-center justify-end pointer-events-none">
          <CanadaMap
            className="w-[75%] max-w-[960px] opacity-[0.45] shrink-0"
            style={{
              maskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.6) 25%, black 50%), linear-gradient(180deg, transparent 0%, black 12%, black 88%, transparent 100%)",
              maskComposite: "intersect",
              WebkitMaskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.6) 25%, black 50%), linear-gradient(180deg, transparent 0%, black 12%, black 88%, transparent 100%)",
              WebkitMaskComposite: "source-in",
            }}
          />
        </div>

        {/* Glass top-edge highlight — the bright rim of a glass sheet */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

        {/* Radial light source — light entering from top-center through glass */}
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[260px] rounded-full bg-white/5 blur-[60px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 pt-40">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 bg-white/8 border border-white/15 rounded-full px-4 py-1.5 mb-8">
            <span className="text-xs font-medium text-white/70 tracking-widest uppercase">
              Property Management & Compliance
            </span>
          </div>

          {/* Main headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.06] tracking-tight mb-6">
            Manage Properties.
            <br />
            <span className="text-primary">
              Stay Compliant.
            </span>
          </h1>

          {/* Sub-headline */}
          <p className="text-lg sm:text-xl text-[#E5E7EB] max-w-2xl mx-auto mb-10 leading-relaxed">
            Stratakey gives strata councils, HOAs, condo boards, and property managers
            the tools to manage rental properties, track bookings, and enforce compliance,
            all from one powerful platform.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link to="/signup" onClick={() => trackCtaClick("hero_cta")}>
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white shadow-2xl shadow-primary/40 hover:shadow-primary/60 transition-all text-base font-semibold px-8 h-12 rounded-lg"
              >
                Start Free 7-Day Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button
                size="lg"
                variant="outline"
                className="border-white/20 text-white/80 bg-white/5 hover:bg-white/10 text-base font-medium px-8 h-12 rounded-lg hover:border-white/30 transition-all"
              >
                View Pricing
              </Button>
            </a>
          </div>

          {/* Trust signals */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/40">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-green-400" />
              Hosts always free
            </span>
            <span className="hidden sm:block text-white/20">•</span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-green-400" />
              Cancel anytime
            </span>
            <span className="hidden sm:block text-white/20">•</span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-green-400" />
              Direct messaging built-in
            </span>
          </div>
        </div>

        {/* Dashboard Preview Card */}
        <div className="mt-20 max-w-5xl mx-auto">
          <p className="text-center text-xs font-medium text-white/30 tracking-widest uppercase mb-5">
            Platform Preview
          </p>
          <div className="relative">
            {/* Glow under card */}
            <div className="absolute inset-x-10 bottom-0 h-40 bg-primary/30 blur-3xl rounded-full" />

            {/* Mock dashboard card */}
            <div className="relative bg-[hsl(217_33%_17%)] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
              {/* Window chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[hsl(222_47%_11%)] border-b border-white/10">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/70" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <div className="w-3 h-3 rounded-full bg-green-500/70" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="bg-white/10 rounded-md px-4 py-0.5 text-xs text-white/50 font-mono">
                    app.stratakey.com/dashboard
                  </div>
                </div>
              </div>

              {/* Mock content */}
              <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: "Active Hosts", value: "24", color: "text-blue-400", icon: Users },
                  { label: "Approved Bookings", value: "142", color: "text-green-400", icon: CalendarCheck },
                  { label: "Compliance Rate", value: "98%", color: "text-emerald-400", icon: Shield },
                  { label: "Nights Tracked", value: "1,847", color: "text-amber-400", icon: BarChart3 },
                ].map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div
                      key={stat.label}
                      role="region"
                      aria-label={`${stat.label}: ${stat.value}`}
                      className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/50 font-medium">{stat.label}</span>
                        <Icon aria-hidden="true" className={`h-3.5 w-3.5 ${stat.color}`} />
                      </div>
                      <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                    </div>
                  );
                })}
              </div>

              {/* Mock table preview */}
              <div className="px-6 pb-6">
                <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
                    <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                      Recent Bookings
                    </span>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">
                      Live
                    </Badge>
                  </div>
                  {[
                    { unit: "Unit 304", host: "J. Smith", nights: "3 nights", status: "Approved", statusColor: "text-green-400" },
                    { unit: "Unit 712", host: "M. Chen", nights: "2 nights", status: "Pending", statusColor: "text-amber-400" },
                    { unit: "Unit 105", host: "A. Patel", nights: "5 nights", status: "Approved", statusColor: "text-green-400" },
                  ].map((row) => (
                    <div
                      key={row.unit}
                      className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors"
                    >
                      <span className="text-xs font-medium text-white/80">{row.unit}</span>
                      <span className="text-xs text-white/50">{row.host}</span>
                      <span className="text-xs text-white/40">{row.nights}</span>
                      <span className={`text-xs font-semibold ${row.statusColor}`}>{row.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────

function StatsBar() {
  const stats = [
    { value: "500+", label: "Buildings Managed", icon: Building2 },
    { value: "99.9%", label: "Platform Uptime", icon: Zap },
    { value: "Messaging", label: "Hosts, Owners & Guests", icon: MessageSquare },
    { value: "7 Days", label: "Free Trial", icon: Lock },
  ];

  return (
    <section className="bg-[hsl(220,40%,5%)] border-y border-white/8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x md:divide-white/10">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="flex flex-col items-center text-center md:px-8 gap-1.5"
              >
                <Icon aria-hidden="true" className="h-4 w-4 text-primary mb-2" />
                <span className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                  {stat.value}
                </span>
                <span className="text-xs sm:text-sm text-white/40 font-medium">
                  {stat.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Features Section ─────────────────────────────────────────────────────────

const features = [
  {
    icon: CalendarCheck,
    title: "Booking Approvals & Night Tracking",
    description:
      "Automatically track nightly stay limits per unit. Approve or deny host booking requests with a single click.",
    accent: "blue",
  },
  {
    icon: MessageSquare,
    title: "Direct Messaging & Announcements",
    description:
      "Send in-app messages and email announcements to hosts, unit owners, or active guests. Broadcast to the whole building or message individually.",
    accent: "emerald",
  },
  {
    icon: Shield,
    title: "Compliance Enforcement Tools",
    description:
      "Set custom night caps, by-law rules, and approval workflows. Automatic alerts when limits are approached.",
    accent: "violet",
  },
  {
    icon: Bell,
    title: "Real-Time Notifications",
    description:
      "Push alerts for new bookings, limit warnings, disputes, and maintenance requests. Stay on top of everything.",
    accent: "blue",
  },
  {
    icon: BarChart3,
    title: "Audit Logs & Exportable Reports",
    description:
      "Every action is logged with timestamps. Export compliance reports as CSV for strata meetings or legal reviews.",
    accent: "emerald",
  },
  {
    icon: Users,
    title: "Team Member Management",
    description:
      "Invite council members, property managers, or admins with granular role-based access control.",
    accent: "violet",
  },
];

const accentStyles: Record<string, { bg: string; icon: string; border: string }> = {
  blue:    { bg: "bg-blue-50 dark:bg-blue-500/10",    icon: "text-blue-600 dark:text-blue-400",    border: "group-hover:border-blue-200 dark:group-hover:border-blue-500/30" },
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-500/10", icon: "text-emerald-600 dark:text-emerald-400", border: "group-hover:border-emerald-200 dark:group-hover:border-emerald-500/30" },
  violet:  { bg: "bg-violet-50 dark:bg-violet-500/10",  icon: "text-violet-600 dark:text-violet-400",  border: "group-hover:border-violet-200 dark:group-hover:border-violet-500/30" },
};

function FeaturesSection() {
  return (
    <section id="features" className="py-24 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge
            variant="outline"
            className="mb-4 text-primary border-primary/30 bg-primary/5 font-medium"
          >
            Platform Features
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
            Everything you need to manage properties and stay compliant
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-lg">
            Purpose-built for strata councils and property managers,
            with compliance tools no other platform offers.
          </p>
        </div>

        {/* Feature cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature) => {
            const Icon = feature.icon;
            const a = accentStyles[feature.accent];
            return (
              <div
                key={feature.title}
                className={`group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700/60 p-6 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 ${a.border}`}
              >
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${a.bg} mb-4 group-hover:scale-105 transition-transform`}>
                  <Icon aria-hidden="true" strokeWidth={1.5} className={`h-6 w-6 ${a.icon}`} />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2 text-base tracking-tight">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── How It Works ─────────────────────────────────────────────────────────────

const steps = [
  {
    number: "01",
    title: "Set up your strata in minutes",
    description:
      "Create your organization, add your units, and configure your by-law rules. Our onboarding wizard walks you through every step.",
    icon: Building2,
    color: "text-blue-500",
    highlight: "bg-blue-500",
  },
  {
    number: "02",
    title: "Invite hosts & configure rules",
    description:
      "Send email invites to your STR hosts. Set annual night caps per unit, approval workflows, and permit validation requirements.",
    icon: Users,
    color: "text-violet-500",
    highlight: "bg-violet-500",
  },
  {
    number: "03",
    title: "Approve bookings & stay compliant",
    description:
      "Review incoming booking requests, track nights used, receive real-time alerts, and export reports for your AGM.",
    icon: ClipboardCheck,
    color: "text-emerald-500",
    highlight: "bg-emerald-500",
  },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge
            variant="outline"
            className="mb-4 text-primary border-primary/30 bg-primary/5 font-medium"
          >
            Simple Onboarding
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
            Up and running in under 10 minutes
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-lg">
            No IT team required. No complex setup. Just a clean, intuitive
            platform that works from day one.
          </p>
        </div>

        <ol className="grid md:grid-cols-3 gap-8 relative list-none">
          {/* Connector line (desktop only) */}
          <div aria-hidden="true" className="hidden md:block absolute top-16 left-1/4 right-1/4 h-px bg-gradient-to-r from-blue-500/50 via-violet-500/50 to-emerald-500/50" />

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <li key={step.number} className="relative text-center md:text-left overflow-hidden">
                {/* Ghost step number — decorative background */}
                <div aria-hidden="true" className="absolute -top-4 -left-2 text-[8rem] font-black text-slate-100 dark:text-slate-800/60 leading-none select-none pointer-events-none tracking-tighter">
                  {step.number}
                </div>

                <div className="relative">
                  <div className="flex md:block justify-center mb-5">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
                      <Icon aria-hidden="true" className="h-5 w-5 text-primary" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">
                      {step.title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

// ─── Pricing Section ──────────────────────────────────────────────────────────

function PricingSection() {
  const [buildings, setBuildings] = useState(1);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { currency, setCurrency, isCAD } = useCurrency();
  const sectionRef = useRef<HTMLElement>(null);
  const pricingViewed = useRef(false);
  const buildingsInitialized = useRef(false);

  const perBuilding    = isCAD ? 28.99 : 21.99;
  const managerMonthly = parseFloat((buildings * perBuilding).toFixed(2));

  // GA4: pricing_view via IntersectionObserver
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !pricingViewed.current) {
          pricingViewed.current = true;
          trackPricingView();
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // GA4: building count changes (skip initial mount)
  useEffect(() => {
    if (!buildingsInitialized.current) { buildingsInitialized.current = true; return; }
    trackBuildingCountChanged(buildings);
  }, [buildings]);

  // Council-exclusive features (shown after "Everything in PM, plus:")
  const councilFeatures = [
    "Guest portal (no login needed)",
    "Full compliance enforcement & by-law rules",
    "AGM-ready audit log & exportable reports",
    "Direct single-building administration",
    "Flat CA$18.99/mo. No per-building fees",
  ];

  const managerFeatures = [
    "Manage multiple buildings (CA$28.99/building)",
    "Oversee all units & hosts per building",
    "Booking approvals & night tracking oversight",
    "Compliance & document review",
    "Cross-building reports & audit log",
    "Amenities & maintenance oversight",
    "Direct messaging to hosts, owners & guests",
    "Real-time notifications",
    "Dispute tracking",
    "Delegated admin access",
    "Priority email support",
  ];

  const hostFeatures = [
    "Submit & track booking requests",
    "Unlimited units & hosts",
    "Booking approvals & night tracking",
    "Upload compliance documents",
    "In-app & email notifications",
    "Message your manager or council",
    "Submit maintenance requests",
    "No monthly fees, ever",
  ];

  const faqs = [
    {
      q: "Can I cancel at any time?",
      a: "Yes! Cancel from your billing page at any time. No contracts, no penalties.",
    },
    {
      q: "Are prices in CAD?",
      a: "Yes. All prices are in Canadian dollars by default. Use the currency toggle above to view USD pricing. GST/HST is calculated and shown at checkout based on your billing address.",
    },
    {
      q: "Does Stratakey work for HOAs and condo associations in the US?",
      a: "Yes! While we're built for Canadian strata councils, Stratakey works perfectly for US Homeowners Associations (HOAs), condo boards, and property managers. All features including compliance tracking, booking approvals, and night cap enforcement apply directly to HOA short-term rental bylaws.",
    },
    {
      q: "What payment methods are accepted?",
      a: "All major credit and debit cards via Stripe. Your card details are never stored on our servers.",
    },
    {
      q: "Is direct messaging included in paid plans?",
      a: "Yes! Every paid plan includes in-app messaging and optional email delivery to hosts, unit owners, and active guests at no extra charge.",
    },
    {
      q: "Do hosts pay anything?",
      a: "Never. Hosts always have a free account with full access to bookings, documents, maintenance requests, and messaging.",
    },
  ];

  return (
    <section id="pricing" ref={sectionRef} className="py-24 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge
            variant="outline"
            className="mb-4 text-primary border-primary/30 bg-primary/5 font-medium"
          >
            Pricing
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
            Simple, transparent pricing
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-lg">
            From solo landlords to strata councils, every plan is transparent and month-to-month. Hosts are always free.
          </p>
          <div className="flex flex-col items-center gap-2 mt-4">
            <CurrencyToggle currency={currency} onChange={setCurrency} />
            <p className="text-sm text-slate-400 dark:text-slate-500">
              {isCAD
                ? "All prices in CAD · Billed monthly · GST/HST may apply"
                : "All prices in USD · Taxes may apply"
              }
            </p>
          </div>
        </div>

        {/* Plan cards — order: Host (free) → Individual → Council → PM (Most Popular) */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-start mb-20">

          {/* ── Host — Free ── */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-700 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 mb-5">
                <Home className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex items-start gap-2 mb-1">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">
                  Host
                </h3>
                <span className="mt-0.5 shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs font-bold px-2 py-0.5 whitespace-nowrap">
                  Always Free
                </span>
              </div>
              <div className="mb-4">
                <span className="text-4xl font-extrabold text-slate-900 dark:text-white">$0</span>
                <span className="text-slate-400 text-sm ml-1.5">/ mo</span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
                Manage your STR independently from day one. No invitation needed. Track bookings, run your guest portal, and upload documents solo. If your building has a strata council or PM, they can invite you to link in for compliance later.
              </p>
              <ul className="space-y-2 mb-8">
                {hostFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                    <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup?role=host" onClick={() => trackPlanSelected("host")}>
                <Button
                  variant="outline"
                  className="w-full min-h-[44px] border-emerald-300 dark:border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 font-medium"
                >
                  Sign Up Free as Host
                  <ChevronRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* ── Individual — $9.99 CAD / $7.99 USD ── */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-primary/40 dark:border-primary/40 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary/40 via-primary to-primary/40 rounded-t-xl" />
            <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
                  <Home className="h-6 w-6 text-primary" />
                </div>
                <Badge className="text-[10px] px-2 py-0.5 bg-primary/10 text-primary border-primary/30">New</Badge>
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Individual</h3>
              <div className="mb-1">
                <span className="text-4xl font-extrabold text-slate-900 dark:text-white">{isCAD ? "CA$9.99" : "US$7.99"}</span>
                <span className="text-slate-400 text-sm ml-1.5">/ mo</span>
              </div>
              <p className="text-xs text-slate-400 mb-3">Up to 5 properties · 7-day free trial</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
                For independent landlords managing their own properties: income tracking, rental calendar, and CSV exports included. No strata needed.
              </p>
              <ul className="space-y-2 mb-8">
                {[
                  "Up to 5 properties",
                  "Booking management & night tracking",
                  "Rental calendar with visual month-view",
                  "Income & expense tracker + CSV export",
                  "Guest portal (no login needed)",
                  "Documents: leases, permits, receipts & more",
                  "Maintenance log & dispute notes",
                  "In-app & email notifications",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                    <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup?role=individual" onClick={() => trackPlanSelected("individual")}>
                <Button className="w-full min-h-[44px] bg-primary hover:bg-primary/90 text-white font-medium">
                  Start Free Trial · {isCAD ? "CA$9.99" : "US$7.99"}/mo
                  <ChevronRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* ── Strata Council — $18.99 CAD / $13.99 USD ── */}
          <div className="bg-[hsl(222_47%_11%)] rounded-xl p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20 border border-primary/30 mb-5">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-1">Strata Council</h3>
              <div className="mb-1">
                <span className="text-4xl font-extrabold">{isCAD ? "CA$18.99" : "US$13.99"}</span>
                <span className="text-white/60 text-sm ml-1.5">/ mo</span>
              </div>
              <p className="text-xs text-[#D1D5DB] mb-3">Flat monthly rate that covers your entire building</p>
              <p className="text-xs text-white/70 mb-3 leading-relaxed">
                Self-manage STR compliance for your building. Full oversight, no third-party required.
              </p>
              <ul className="space-y-2 mb-8">
                {councilFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-white/80">
                    <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 text-green-400 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup?role=strata_admin" onClick={() => trackPlanSelected("strata_council")}>
                <Button className="w-full min-h-[44px] bg-primary hover:bg-primary/90 text-white font-medium">
                  Start as Strata Council
                  <ChevronRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* ── Property Manager — Most Popular ── */}
          <div className="bg-primary rounded-xl p-8 text-white relative md:-mt-4 shadow-xl shadow-primary/25 pt-10">
            {/* Most Popular badge — above overflow-hidden boundary */}
            <div className="absolute -top-3 inset-x-0 flex justify-center pointer-events-none">
              <span aria-label="Most popular plan" className="inline-flex items-center rounded-full bg-white text-primary px-3 py-1 text-xs font-semibold shadow-md whitespace-nowrap tracking-wide">
                Most Popular
              </span>
            </div>
            {/* Background decoration — isolated with overflow-hidden */}
            <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/5 rounded-full" />
            </div>
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 border border-white/30 mb-5">
                <UserCog className="h-6 w-6 text-white" />
              </div>
              <div className="flex items-start gap-2 mb-1">
                <h3 className="text-xl font-bold leading-tight">Property Manager</h3>
                <span className="mt-0.5 shrink-0 rounded-full bg-white/20 text-white text-xs font-bold px-2 py-0.5 whitespace-nowrap">
                  Multi-building
                </span>
              </div>
              <div aria-live="polite" aria-atomic="true" className="mb-1">
                <span className="text-4xl font-extrabold">{isCAD ? "CA$" : "US$"}{managerMonthly}</span>
                <span className="text-white/60 text-sm ml-1.5">/ mo</span>
              </div>
              <p className="text-xs text-white/60 mb-4">
                {isCAD ? "CA$" : "US$"}{perBuilding} × {buildings} building{buildings !== 1 ? "s" : ""} = {isCAD ? "CA$" : "US$"}{managerMonthly}/mo
              </p>
              {/* Building stepper */}
              <div className="mb-5">
                <p className="text-xs font-medium text-white/60 mb-2">How many buildings?</p>
                <div className="inline-flex items-center gap-3 rounded-lg bg-white/10 border border-white/20 px-3 py-1.5">
                  <button
                    type="button"
                    className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md hover:bg-white/20 text-white/70 hover:text-white transition-colors disabled:opacity-40"
                    onClick={() => setBuildings((b) => Math.max(1, b - 1))}
                    disabled={buildings <= 1}
                    aria-label="Decrease building count"
                  >
                    <Minus aria-hidden="true" className="h-3.5 w-3.5" />
                  </button>
                  <span className="min-w-[1.5rem] text-center text-sm font-bold tabular-nums text-white">
                    {buildings}
                  </span>
                  <button
                    type="button"
                    className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md hover:bg-white/20 text-white/70 hover:text-white transition-colors disabled:opacity-40"
                    onClick={() => setBuildings((b) => Math.min(50, b + 1))}
                    disabled={buildings >= 50}
                    aria-label="Increase building count"
                  >
                    <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-white/80 mb-3 leading-relaxed">
                Oversee multiple strata buildings from one powerful dashboard.
              </p>
              <p className="text-xs font-semibold text-white/50 mb-3 italic">
                Everything in Strata Council, plus:
              </p>
              <ul className="space-y-2 mb-8">
                {managerFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-white/90">
                    <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 text-white/70 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup?role=property_manager" onClick={() => trackPlanSelected("property_manager")}>
                <Button className="w-full min-h-[44px] bg-white text-primary hover:bg-white/90 font-semibold">
                  Start as Property Manager
                  <ChevronRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h3 className="text-lg font-bold text-center text-slate-900 dark:text-white mb-6">
            Frequently asked questions
          </h3>
          <dl className="space-y-2">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => {
                    const opening = openFaq !== i;
                    setOpenFaq(opening ? i : null);
                    if (opening) trackFaqOpened(faq.q);
                  }}
                  className="w-full flex cursor-pointer items-center justify-between px-5 py-4 text-left hover:bg-white dark:hover:bg-slate-800/50 transition-colors"
                >
                  <span className="text-sm font-semibold text-slate-900 dark:text-white pr-4">
                    {faq.q}
                  </span>
                  <ChevronRight
                    className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${
                      openFaq === i ? "rotate-90" : ""
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 border-t border-slate-100 dark:border-slate-800">
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed pt-3">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </dl>
        </div>

      </div>
    </section>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

const testimonials = [
  {
    quote:
    "Stratakey transformed how we handle STR compliance. What used to take our council hours of spreadsheet work now takes minutes. The direct messaging feature alone saves us dozens of emails every month.",
    name: "Sarah Thompson",
    role: "Strata Council President",
    building: "The Metropolitan",
    initials: "ST",
  },
  {
    quote:
      "Managing 12 buildings used to be chaotic. Now I have one dashboard, one login, and complete visibility across my entire portfolio. Night tracking is incredibly accurate.",
    name: "David Nguyen",
    role: "Property Manager",
    building: "Westcoast Strata Services",
    initials: "DN",
  },
  {
    quote:
      "As a host, I appreciate how transparent the approval process is. I submit my booking, get instant status updates, and the guest portal link makes check-in seamless.",
    name: "Amanda Kowalski",
    role: "STR Host",
    building: "Harbour View Towers",
    initials: "AK",
  },
];

function Testimonials() {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": testimonials.map((t, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
        },
        "author": {
          "@type": "Person",
          "name": t.name,
          "jobTitle": t.role,
        },
        "reviewBody": t.quote,
        "itemReviewed": {
          "@type": "SoftwareApplication",
          "name": "Stratakey",
          "applicationCategory": "BusinessApplication",
        },
      },
    })),
  };

  return (
    <section className="py-24 bg-white dark:bg-slate-900">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge
            variant="outline"
            className="mb-4 text-primary border-primary/30 bg-primary/5 font-medium"
          >
            Trusted by Professionals
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
            Loved by strata professionals
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-lg">
            Strata councils and property managers around the world
            trust Stratakey to keep them compliant.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t) => (
            <div
              key={t.name}
              className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-700/60 flex flex-col gap-4 hover:border-slate-300 hover:shadow-sm transition-all duration-200"
            >
              {/* Stars */}
              <div aria-label="5 out of 5 stars" className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star aria-hidden="true" key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed flex-1 italic">
                "{t.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <div
                  aria-label={t.name}
                  className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 text-xs font-semibold shrink-0 tracking-wide"
                >
                  {t.initials}
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-white tracking-tight">
                    {t.name}
                  </div>
                  <div className="text-xs text-slate-400 dark:text-slate-500">
                    {t.role} · {t.building}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CTA Banner ───────────────────────────────────────────────────────────────

function CTABanner() {
  return (
    <section className="relative overflow-hidden bg-[hsl(222_47%_11%)] py-20">
      {/* Background glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/15 rounded-full blur-[80px]" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 mb-6">
          <Sparkles aria-hidden="true" className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-medium text-white/90 tracking-wide">
            Start your free trial today
          </span>
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
          Ready to simplify property
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-primary to-indigo-400 bg-clip-text text-transparent">
            management and compliance?
          </span>
        </h2>

        <p className="text-white/60 text-lg max-w-xl mx-auto mb-10">
          Join strata councils and property managers who switched
          to a smarter way to manage properties and stay compliant.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <Link to="/signup" onClick={() => trackCtaClick("footer_cta")}>
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white shadow-2xl shadow-primary/40 hover:shadow-primary/60 transition-all text-base font-semibold px-10 h-12 rounded-xl"
            >
              Start Free 7-Day Trial
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <a href="mailto:info@stratakey.ca">
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white bg-white/5 hover:bg-white/15 backdrop-blur text-base font-medium px-8 h-12 rounded-xl hover:border-white/50 transition-all"
            >
              Contact Sales
            </Button>
          </a>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/40">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-green-400" />
            7-day free trial
          </span>
          <span className="hidden sm:block text-white/20">•</span>
          <span className="flex items-center gap-1.5">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-green-400" />
            Hosts always free
          </span>
          <span className="hidden sm:block text-white/20">•</span>
          <span className="flex items-center gap-1.5">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-green-400" />
            Cancel anytime
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function LandingFooter() {
  const year = new Date().getFullYear();
  const { user } = useAuth();

  return (
    <footer className="bg-[hsl(224_71%_4%)] text-white/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5 mb-4 group">
              <img
                src="/logo.png"
                alt="Stratakey"
                className="h-8 w-auto object-contain rounded-xl group-hover:scale-105 transition-transform"
              />
              <span className="font-bold text-lg text-white tracking-tight">
                Stratakey
              </span>
            </Link>
            <p className="text-sm text-white/50 leading-relaxed mb-4">
              Property management and compliance platform for strata
              councils and property managers worldwide.
            </p>
            <a
              href="mailto:info@stratakey.ca"
              className="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              info@stratakey.ca
            </a>
          </div>

          {/* Product column */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Product</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="#features" className="hover:text-white transition-colors">
                  Features
                </a>
              </li>
              <li>
                <a href="#pricing" className="hover:text-white transition-colors">
                  Pricing
                </a>
              </li>
              <li>
                <a href="#how-it-works" className="hover:text-white transition-colors">
                  How It Works
                </a>
              </li>
              <li>
                <a href="#hosts" className="hover:text-white transition-colors">
                  For Hosts
                </a>
              </li>
            </ul>
          </div>

          {/* Account column */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Account</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/login" className="hover:text-white transition-colors">
                  Sign In
                </Link>
              </li>
              <li>
                <Link to="/signup" className="hover:text-white transition-colors">
                  Get Started Free
                </Link>
              </li>
              <li>
                <Link to="/signup?role=strata_admin" className="hover:text-white transition-colors">
                  Strata Council
                </Link>
              </li>
              <li>
                <Link to="/signup?role=property_manager" className="hover:text-white transition-colors">
                  Property Manager
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal column */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Legal</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <a
                  href="mailto:info@stratakey.ca"
                  className="hover:text-white transition-colors"
                >
                  Support
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/30">
          <span>© {year} Stratakey. All rights reserved.</span>
          <span>Built for strata communities worldwide.</span>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page Component ───────────────────────────────────────────────────────

export default function LandingPage() {
  const { user, isLoading } = useAuth();
  const { isActive, requiresSubscription, isLoading: subLoading } = useSubscription();

  // Only redirect to /dashboard if the user can actually access it.
  // Paid roles (strata_admin / property_manager) without an active subscription
  // would be immediately bounced back to /pricing by ProtectedRoute, creating a
  // redirect loop. Show the landing page to them instead so they can subscribe.
  const canAccessDashboard = !!user && (!requiresSubscription || isActive);

  // Wait for both auth and subscription to resolve before deciding.
  const stillLoading = isLoading || (!!user && requiresSubscription && subLoading);

  // GA4: scroll depth + outbound link tracking for landing page
  useEffect(() => {
    const cleanScroll   = initScrollDepthTracking();
    const cleanOutbound = initOutboundLinkTracking();
    return () => { cleanScroll(); cleanOutbound(); };
  }, []);

  if (!stillLoading && canAccessDashboard) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-white focus:text-black focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to main content
      </a>
      <Navbar />
      <Hero />
      <StatsBar />
      <FeaturesSection />
      <HowItWorks />
      <PricingSection />
      <Testimonials />
      <CTABanner />
      <LandingFooter />
    </div>
  );
}
